/*
 * Pincode and delivery-serviceability measurement (#174, GA4-E5-09).
 *
 * WHY THIS ONE MATTERS MORE THAN ITS PRIORITY SUGGESTS. Serviceability is the gate on every order.
 * A customer who enters a pincode we do not deliver to is two things at once: a sale we just lost,
 * and the only honest signal we have about where demand exists that we do not serve. Neither was
 * being captured. "Which pincodes ask and are turned away, and how often" is a question the business
 * cannot answer today and could act on directly — it is an expansion shortlist, ranked by demand.
 *
 * IS A PINCODE PERSONAL DATA? No, and it is worth stating rather than assuming. An Indian PIN covers
 * thousands of households; GA4 already receives coarser-grained geography than this by default. It is
 * not an identifier, it is not joined to one here, and `analytics-click-attrs`' rule — no name,
 * address, email, phone or payment detail — is not touched. A full address would be a different
 * matter, and nothing here reads one.
 *
 * HOOKED, NOT REBUILT. `snippets/pincode-popup.liquid` says in its own header that anything needing
 * to react to a pincode should listen for the `om:pincode` event rather than add another copy of its
 * logic — that file exists because handlers had been stacking. So this listens; it does not touch it.
 *
 * ── WHY THE DEDUP LIVES IN sessionStorage (#382) ────────────────────────────────────────────────
 * The guard used to be a plain variable in this closure, which is reset by every navigation. So
 * `applyPincode` re-announcing the stored pincode on each page load produced one `pincode_set` per
 * PAGE VIEW, every one of them labelled `pincode_is_change: false` — the opposite of the "report the
 * change, not the state" intent written into the guard's own comment. A session of ten page views
 * reported ten pincode entries, which is the metric the guard existed to prevent. It is now
 * remembered in sessionStorage, so the dedup survives the navigation it was always meant to outlast.
 *
 * ── AND WHY A MANUAL ENTRY CARRIES A MARKER (#383) ──────────────────────────────────────────────
 * `submitPincode()` reloads the page immediately after applying a typed pincode. The dataLayer push
 * is synchronous but GTM's tag firing and GA4's transport are not, so the reload tore the document
 * down before the hit reliably left — losing the one signal in this flow that is unambiguous shopper
 * intent, as opposed to an IP lookup or a geolocation guess. Worse, the entries that did survive were
 * re-read from storage on the next page and reported as `pincode_is_change: false`, so a change was
 * recorded as if it were not one.
 *
 * The popup now writes an intent marker before it reloads and this consumes it on the way back —
 * the same arrangement `analytics-auth.js` uses to report a login only once the next page confirms
 * it. The marker deliberately BYPASSES the dedup above: re-entering the pincode you already have is
 * still a deliberate act, and #383 calls that case out specifically.
 *
 * ── THE MARKER MUST OUTLIVE THE PAGE THAT WROTE IT ──────────────────────────────────────────────
 * `applyPincode` dispatches `om:pincode` SYNCHRONOUSLY, and `submitPincode` calls it after leaving
 * the marker — so without `intentWrittenFor` below, this listener ran on the doomed page: it
 * consumed the marker, pushed the hit the reload was about to destroy, and left the next page with
 * nothing to report and a dedup that correctly suppressed the re-announcement. That is worse than
 * the bug it replaced, because before the fix the closure reset meant the next page at least
 * re-reported the entry, mislabelled but present.
 *
 * The flag is deliberately a plain closure variable — the navigation resets it, which is the very
 * property that caused both bugs and is here the one that fixes this. It holds the PINCODE rather
 * than a boolean, so an unrelated value arriving on the doomed page is still reported, and it is set
 * only when the marker was actually written: if storage is unavailable there is no marker for the
 * next page to find, and suppressing the push here would lose the entry entirely.
 */
(function () {
  'use strict';

  var OMPincodeAnalytics = {};

  var SEEN_KEY = 'om_pincode_seen';
  var INTENT_KEY = 'om_pincode_intent';

  // The pincode this page wrote a marker for, if any. Reset by the navigation, which is the point.
  var intentWrittenFor = null;
  // A reload is immediate; anything older than this is not the entry that caused it.
  var INTENT_TTL_MS = 5 * 60 * 1000;

  /* ── Storage, which is allowed to be absent ────────────────────────────────────────────────── */

  function store() {
    try { return window.sessionStorage; } catch (e) { return null; }  // Safari private mode throws
  }

  OMPincodeAnalytics.readSeen = function (s) {
    if (!s) return null;
    try { return s.getItem(SEEN_KEY); } catch (e) { return null; }
  };

  OMPincodeAnalytics.writeSeen = function (s, pincode) {
    if (!s) return;
    try { s.setItem(SEEN_KEY, String(pincode)); } catch (e) { /* quota or disabled */ }
  };

  /*
   * Consume before validating: a malformed or expired marker must not be able to sit there and
   * attribute some unrelated later entry. Same rule as `analytics-auth.js`.
   */
  OMPincodeAnalytics.takeIntent = function (s, now) {
    if (!s) return null;
    var raw;
    try { raw = s.getItem(INTENT_KEY); } catch (e) { return null; }
    if (!raw) return null;
    try { s.removeItem(INTENT_KEY); } catch (e) { /* nothing useful to do */ }

    var marker;
    try { marker = JSON.parse(raw); } catch (e) { return null; }
    if (!marker || !marker.pincode) return null;
    if (!marker.at || (now - marker.at) > INTENT_TTL_MS) return null;
    return marker;
  };

  /**
   * Records that the customer just TYPED this pincode, for the page load that follows the reload.
   *
   * Exposed so `snippets/pincode-popup.liquid` can call it instead of writing the key itself: two
   * copies of a storage key is how the writer and the reader drift apart, and this one is only
   * meaningful if both halves agree.
   */
  OMPincodeAnalytics.rememberIntent = function (pincode) {
    var s = store();
    if (!s || !pincode) return;
    try {
      s.setItem(INTENT_KEY, JSON.stringify({
        pincode: String(pincode),
        at: new Date().getTime()
      }));
      // Only now: if the write failed there is no marker for the next page, so this page's push is
      // the entry's only chance and must not be suppressed.
      intentWrittenFor = String(pincode);
    } catch (e) { /* quota or disabled - the entry simply goes unreported, as it did before */ }
  };

  /* ── The decision, kept pure so the rules are pinned by tests rather than asserted in review ── */

  /**
   * Given the pincode that just arrived, the one already reported this session, and any manual-entry
   * marker, decide whether to report and how to label it.
   *
   * Returns null when nothing should be reported.
   */
  OMPincodeAnalytics.classify = function (pincode, seen, intent) {
    if (!pincode) return null;
    pincode = String(pincode);

    // A typed entry always reports, even when it repeats the pincode already held: the dedup exists
    // to suppress a page load re-announcing state, not to suppress a shopper doing something.
    if (intent && String(intent.pincode) === pincode) {
      return { pincode: pincode, isChange: true };
    }

    if (seen !== null && seen !== undefined && String(seen) === pincode) return null;

    // `isChange` is false only for the first pincode observed in the session — after that, a
    // different value arriving is by definition a change.
    return { pincode: pincode, isChange: seen !== null && seen !== undefined };
  };

  window.OMPincodeAnalytics = OMPincodeAnalytics;

  /* ── DOM ──────────────────────────────────────────────────────────────────────────────────── */

  if (typeof document === 'undefined' || !document.addEventListener) return;

  function dataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  function currentPincode() {
    try { return window.localStorage.getItem('userPincode') || null; } catch (e) { return null; }
  }

  document.addEventListener('om:pincode', function (e) {
    var pincode = e && e.detail && e.detail.pincode;
    if (!pincode) return;

    // This page wrote the marker and is about to reload. Reporting here would consume the marker and
    // push a hit the teardown destroys, leaving the next page nothing to report. Let it through.
    if (intentWrittenFor !== null && intentWrittenFor === String(pincode)) return;

    var s = store();
    var decision = OMPincodeAnalytics.classify(
      pincode,
      OMPincodeAnalytics.readSeen(s),
      OMPincodeAnalytics.takeIntent(s, new Date().getTime())
    );
    if (!decision) return;

    OMPincodeAnalytics.writeSeen(s, decision.pincode);
    dataLayer().push({
      event: 'pincode_set',
      pincode: decision.pincode,
      pincode_is_change: decision.isChange,
      // One GTM tag sends both delivery events with all three parameters, and GTM merges pushes, so
      // the other event's keys are cleared rather than inherited (#705).
      delivery_state: undefined,
      serviceable: undefined
    });
  });

  /*
   * A pincode TYPED somewhere that does not reload: the PDP's inline "Check" (#638).
   *
   * `submitPdpPincode()` in `snippets/custom-stock-check.liquid` writes localStorage directly and
   * never goes through `applyPincode`, so no `om:pincode` fires and the entry went unreported on the
   * page it happened on. It surfaced one page later as an `applyPincode` re-announcement, labelled
   * `pincode_is_change: false`, which is the #383 mislabel by another door.
   *
   * There is no reload here, so no marker either: the push happens now, with the same rule as a
   * popup entry (a typed pincode always reports, and always as a change), and the seen value is
   * written so the next page's re-announcement stays deduplicated.
   */
  OMPincodeAnalytics.reportTyped = function (pincode) {
    var s = store();
    var decision = OMPincodeAnalytics.classify(
      pincode,
      OMPincodeAnalytics.readSeen(s),
      pincode ? { pincode: String(pincode) } : null
    );
    if (!decision) return;

    OMPincodeAnalytics.writeSeen(s, decision.pincode);
    dataLayer().push({
      event: 'pincode_set',
      pincode: decision.pincode,
      pincode_is_change: decision.isChange,
      // Shares the delivery tag with delivery_check, and GTM merges pushes: clear its keys (#705).
      delivery_state: undefined,
      serviceable: undefined
    });
  };

  // The delivery verdict, from the PDP's own state machine. Only terminal states are reported:
  // 'checking' is a spinner, not an answer, and 'no-pincode' is the absence of a question.
  // Deduped because the state machine re-runs on variant switches and quantity changes — #163 and
  // #164 are open for exactly this kind of repeat firing, and this must not become a third.
  var TERMINAL = { 'in-stock': 1, 'out-of-stock': 1, 'not-serviceable': 1 };
  var lastVerdict = null;
  document.addEventListener('om:delivery-state', function (e) {
    var state = e && e.detail && e.detail.state;
    if (!state || !TERMINAL[state]) return;
    var pincode = currentPincode();
    var key = state + '|' + pincode;
    if (key === lastVerdict) return;
    lastVerdict = key;
    dataLayer().push({
      event: 'delivery_check',
      delivery_state: state,
      serviceable: state !== 'not-serviceable',
      pincode: pincode ? String(pincode) : null,
      pincode_is_change: undefined  // see pincode_set above (#705)
    });
  });
})();
